import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Copy, ExternalLink, Trash2, Sparkles, Users, Leaf, ChevronRight, Loader2, X, BookOpen, Cake, Plane, Heart, Calendar, Star, Award, GraduationCap, Baby, Wand2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = { cake: Cake, plane: Plane, heart: Heart, calendar: Calendar, star: Star, award: Award, book: GraduationCap, baby: Baby, sparkles: Wand2 };

const OccasionDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [occasionTypes, setOccasionTypes] = useState([]);
  const [form, setForm] = useState({ subject_name: "", occasion_type: "", title: "", event_description: "" });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const [userRes, eventsRes, typesRes] = await Promise.all([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/occasions`),
          axios.get(`${API}/occasions/types`)
        ]);
        setUser(userRes.data);
        setEvents(eventsRes.data.events);
        setOccasionTypes(typesRes.data.types);
      } catch {
        navigate("/login");
      }
      setLoading(false);
    };
    init();
  }, [navigate]);

  const fetchEvents = async () => {
    const res = await axios.get(`${API}/occasions`);
    setEvents(res.data.events);
  };

  const handleCreate = async () => {
    if (!form.subject_name.trim() || !form.occasion_type) {
      toast.error("Please fill in the subject name and occasion type");
      return;
    }
    if (form.occasion_type === "custom" && !form.event_description.trim()) {
      toast.error("Please describe your custom event");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/occasions`, form);
      toast.success("Event created!");
      setShowCreate(false);
      setForm({ subject_name: "", occasion_type: "", title: "", event_description: "" });
      await fetchEvents();
      setSelectedEvent(res.data.event);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
    }
    setCreating(false);
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/occasions/${eventId}`);
      toast.success("Event deleted");
      setSelectedEvent(null);
      await fetchEvents();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleViewEvent = async (event) => {
    setSelectedEvent(event);
    try {
      const res = await axios.get(`${API}/occasions/${event.id}`);
      setContributions(res.data.contributions);
    } catch {}
  };

  const handleGenerate = async (eventId) => {
    setGenerating(eventId);
    try {
      const res = await axios.post(`${API}/occasions/${eventId}/generate`);
      toast.success(`Story generated with ${res.data.chapter_count} chapters! Taking you there...`);
      // The backend already activated the book — navigate to see it
      setTimeout(() => navigate("/app"), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed. Try again.");
    }
    setGenerating(null);
  };

  const getShareUrl = (slug) => `${window.location.origin}/contribute/${slug}`;

  const copyUrl = async (slug) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(slug));
      toast.success("Link copied!");
    } catch { toast.error("Failed to copy"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F6F1] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#7A9B7E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6F1]" data-testid="occasion-dashboard">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#F8F6F1]/80 backdrop-blur-xl border-b border-[#D4C9B8]/30">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#9FB89F] to-[#7A9B7E] flex items-center justify-center">
                <Leaf className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif text-lg font-medium text-[#2D3A2D]">My Story</span>
            </Link>
            <div className="h-5 w-px bg-[#D4C9B8]" />
            <span className="font-sans text-sm font-medium text-[#4A5D4A]">Collaborative Events</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/app" className="font-sans text-xs text-[#7A8A7A] hover:text-[#4A5D4A] transition-colors">My Memoir</Link>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-[#4A5D4A] text-white rounded-full font-sans text-sm hover:bg-[#2D3A2D] transition-colors" data-testid="create-event-btn">
              <Plus className="w-4 h-4" /> New Event
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Events list */}
          <div className="lg:col-span-4">
            <h2 className="font-serif text-2xl font-medium text-[#2D3A2D] mb-6">Your Events</h2>
            {events.length === 0 ? (
              <div className="bg-white/60 border border-[#D4C9B8]/40 rounded-2xl p-8 text-center">
                <Users className="w-10 h-10 text-[#9FB89F] mx-auto mb-3" />
                <p className="font-sans text-sm text-[#7A8A7A] mb-4">Create your first collaborative event!</p>
                <button onClick={() => setShowCreate(true)} className="font-sans text-sm text-[#7A9B7E] font-medium hover:underline">
                  Get started
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((ev) => {
                  const Icon = ICON_MAP[ev.icon] || Star;
                  const isActive = selectedEvent?.id === ev.id;
                  return (
                    <button
                      key={ev.id}
                      onClick={() => handleViewEvent(ev)}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${isActive ? "bg-white shadow-md border-[#9FB89F]/50" : "bg-white/40 border-[#D4C9B8]/30 hover:bg-white/70"}`}
                      data-testid={`event-card-${ev.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="font-sans text-sm font-medium text-[#2D3A2D] truncate">{ev.title}</p>
                          <p className="font-sans text-xs text-[#7A8A7A] mt-0.5">{ev.contribution_count} contribution{ev.contribution_count !== 1 ? "s" : ""}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-sans ${ev.status === "complete" ? "bg-[#D1FAE5] text-[#059669]" : ev.status === "generating" ? "bg-[#FEF3C7] text-[#B45309]" : "bg-[#DBEAFE] text-[#2563EB]"}`}>
                            {ev.status}
                          </span>
                          <ChevronRight className="w-4 h-4 text-[#D4C9B8]" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Event detail */}
          <div className="lg:col-span-8">
            {selectedEvent ? (
              <div className="bg-white/70 backdrop-blur-sm border border-[#D4C9B8]/40 rounded-3xl overflow-hidden" data-testid="event-detail">
                {/* Header */}
                <div className="bg-gradient-to-br from-[#9FB89F] to-[#5A7B5E] p-8 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-serif text-3xl font-medium mb-1">{selectedEvent.title}</h2>
                      <p className="font-sans text-white/80 text-sm">For {selectedEvent.subject_name} &middot; {selectedEvent.contribution_count} contributions</p>
                    </div>
                    <button onClick={() => handleDelete(selectedEvent.id)} className="p-2 hover:bg-white/20 rounded-xl transition-colors" title="Delete event">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {/* Share link */}
                  <div>
                    <label className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider block mb-2">Share this link with contributors</label>
                    <div className="flex items-center gap-2 bg-[#F8F6F1] rounded-xl p-3 border border-[#D4C9B8]/40">
                      <input type="text" value={getShareUrl(selectedEvent.slug)} readOnly className="flex-1 bg-transparent font-sans text-sm text-[#4A5D4A] outline-none truncate" data-testid="share-link-input" />
                      <button onClick={() => copyUrl(selectedEvent.slug)} className="p-2 bg-[#7A9B7E] text-white rounded-lg hover:bg-[#6A8B6E] transition-colors" data-testid="copy-link-btn">
                        <Copy className="w-4 h-4" />
                      </button>
                      <a href={getShareUrl(selectedEvent.slug)} target="_blank" rel="noopener noreferrer" className="p-2 bg-[#E5D4C1] text-[#6A5A4A] rounded-lg hover:bg-[#D4C9B8] transition-colors">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  {/* Generate button */}
                  {selectedEvent.status === "collecting" && selectedEvent.contribution_count > 0 && (
                    <button
                      onClick={() => handleGenerate(selectedEvent.id)}
                      disabled={generating === selectedEvent.id}
                      className="w-full py-4 bg-gradient-to-r from-[#C67B5C] to-[#B66B4C] text-white rounded-2xl font-sans font-medium text-base flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-60 shadow-lg"
                      data-testid="generate-story-btn"
                    >
                      {generating === selectedEvent.id ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Weaving story from {selectedEvent.contribution_count} contributions...</>
                      ) : (
                        <><Sparkles className="w-5 h-5" /> Generate Collaborative Story</>
                      )}
                    </button>
                  )}

                  {selectedEvent.status === "complete" && selectedEvent.generated_book_id && (
                    <div className="bg-[#D1FAE5]/30 border border-[#059669]/20 rounded-2xl p-5 flex items-center justify-between">
                      <div>
                        <p className="font-sans text-sm font-medium text-[#059669]">Story generated!</p>
                        <p className="font-sans text-xs text-[#7A8A7A] mt-1">View and edit it in your memoir library</p>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/books/${selectedEvent.generated_book_id}/activate`);
                            navigate("/app");
                          } catch { navigate("/app"); }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-[#059669] text-white rounded-full font-sans text-sm hover:bg-[#047857] transition-colors"
                        data-testid="view-story-btn"
                      >
                        <BookOpen className="w-4 h-4" /> View Story
                      </button>
                    </div>
                  )}

                  {/* Contributions */}
                  <div>
                    <h3 className="font-sans text-sm font-medium text-[#4A5D4A] mb-4">Contributions ({contributions.length})</h3>
                    {contributions.length === 0 ? (
                      <p className="font-sans text-sm text-[#7A8A7A] italic">No contributions yet. Share the link above!</p>
                    ) : (
                      <div className="space-y-4">
                        {contributions.map((c) => (
                          <div key={c.id} className="bg-[#F8F6F1] border border-[#D4C9B8]/30 rounded-2xl p-5" data-testid={`contribution-${c.id}`}>
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 rounded-full bg-[#9FB89F]/30 flex items-center justify-center">
                                <Users className="w-4 h-4 text-[#7A9B7E]" />
                              </div>
                              <span className="font-sans text-sm font-medium text-[#2D3A2D]">{c.contributor_name}</span>
                            </div>
                            {c.responses?.map((r, i) => (
                              <div key={i} className="mb-3 last:mb-0">
                                <p className="font-sans text-xs text-[#7A8A7A] mb-1">{r.prompt}</p>
                                <p className="font-sans text-sm text-[#4A5D4A] leading-relaxed">{r.answer}</p>
                              </div>
                            ))}
                            {c.photo_urls?.length > 0 && (
                              <div className="flex gap-2 mt-3 flex-wrap">
                                {c.photo_urls.map((url, i) => (
                                  <img key={i} src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#D4C9B8]/40" />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[300px]">
                <div className="text-center">
                  <Users className="w-12 h-12 text-[#D4C9B8] mx-auto mb-3" />
                  <p className="font-sans text-sm text-[#7A8A7A]">Select an event or create a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }} className="bg-[#F8F6F1] rounded-3xl max-w-lg w-full shadow-2xl border border-[#D4C9B8]/40" onClick={(e) => e.stopPropagation()} data-testid="create-event-modal">
              <div className="flex items-center justify-between p-6 border-b border-[#D4C9B8]/30">
                <h2 className="font-serif text-2xl font-medium text-[#2D3A2D]">Create Collaborative Event</h2>
                <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-[#E5D4C1]/50 rounded-xl"><X className="w-5 h-5 text-[#7A8A7A]" /></button>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-2 block">Who is this for?</label>
                  <input type="text" value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} placeholder="e.g., Sarah, Mom, John & Jane" className="w-full px-4 py-3 rounded-xl bg-white border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="subject-name-input" />
                </div>
                <div>
                  <label className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-2 block">Occasion</label>
                  <div className="grid grid-cols-4 gap-2">
                    {occasionTypes.map((ot) => {
                      const Icon = ICON_MAP[ot.icon] || Star;
                      const isSelected = form.occasion_type === ot.id;
                      return (
                        <button key={ot.id} onClick={() => setForm({ ...form, occasion_type: ot.id })} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${isSelected ? "border-[#7A9B7E] bg-[#9FB89F]/10" : "border-transparent bg-white hover:border-[#D4C9B8]"}`} data-testid={`occasion-type-${ot.id}`}>
                          <Icon className={`w-5 h-5 ${isSelected ? "text-[#7A9B7E]" : "text-[#7A8A7A]"}`} />
                          <span className={`font-sans text-xs ${isSelected ? "text-[#4A5D4A] font-medium" : "text-[#7A8A7A]"}`}>{ot.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {form.occasion_type === "custom" && (
                  <div>
                    <label className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-2 block">Event Description</label>
                    <textarea
                      value={form.event_description}
                      onChange={(e) => setForm({ ...form, event_description: e.target.value })}
                      placeholder="Describe the occasion in detail. The AI will use this to generate personalized questions for contributors..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E] resize-y min-h-[80px]"
                      data-testid="event-description-input"
                    />
                    <p className="font-sans text-xs text-[#9FB89F] mt-1">The AI will generate tailored questions based on this description.</p>
                  </div>
                )}
                <div>
                  <label className="font-sans text-xs text-[#7A8A7A] uppercase tracking-wider mb-2 block">Custom title (optional)</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={form.subject_name ? `${form.subject_name}'s ${occasionTypes.find(o => o.id === form.occasion_type)?.name || "Event"}` : "Auto-generated"} className="w-full px-4 py-3 rounded-xl bg-white border border-[#D4C9B8] font-sans text-sm outline-none focus:border-[#7A9B7E]" data-testid="event-title-input" />
                </div>
              </div>
              <div className="p-6 border-t border-[#D4C9B8]/30 flex gap-3">
                <button onClick={() => setShowCreate(false)} className="flex-1 py-3 rounded-full font-sans text-sm text-[#7A8A7A] hover:bg-[#E5D4C1]/50 transition-colors">Cancel</button>
                <button onClick={handleCreate} disabled={creating || !form.subject_name.trim() || !form.occasion_type || (form.occasion_type === "custom" && !form.event_description.trim())} className="flex-1 py-3 bg-[#4A5D4A] text-white rounded-full font-sans text-sm font-medium hover:bg-[#2D3A2D] transition-colors disabled:opacity-50" data-testid="create-event-confirm">
                  {creating ? "Creating..." : "Create Event"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OccasionDashboard;
